export interface PdfCacheEntry {
  id: string;
  images: string[];
  texts: string[];
  aspect: number;
  updatedAt: number;
}

const DB_NAME = 'documents_pdf_cache_v1';
const DB_VERSION = 1;
const STORE_NAME = 'pdf_pages';

const isIndexedDBAvailable = () =>
  typeof indexedDB !== 'undefined' && typeof window !== 'undefined';

const openDb = (): Promise<IDBDatabase> => {
  if (!isIndexedDBAvailable()) {
    return Promise.reject(new Error('IndexedDB is not available'));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

export const getCachedPdfEntry = async (
  id: string,
): Promise<PdfCacheEntry | undefined> => {
  try {
    const db = await openDb();
    return await new Promise<PdfCacheEntry | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => {
        resolve(req.result as PdfCacheEntry | undefined);
      };
      req.onerror = () => {
        reject(req.error ?? new Error('Failed to read cache'));
      };
    });
  } catch {
    return undefined;
  }
};

export const setCachedPdfEntry = async (
  id: string,
  data: { images: string[]; texts: string[]; aspect: number },
): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const entry: PdfCacheEntry = {
        id,
        images: data.images,
        texts: data.texts,
        aspect: data.aspect,
        updatedAt: Date.now(),
      };
      const req = store.put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error('Failed to write cache'));
    });
  } catch {
    // Swallow caching errors to avoid breaking the viewer if IndexedDB is unavailable
  }
};
